import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { ToastBarService } from '../services/toast-bar.service';
import { TemplateService } from '../services/template.service';
import { catchError, filter, map, of } from 'rxjs';
import { GlobalTemplateService } from '../services/global-template.service';

/**
 * Guard that checks if a valid template ID is present in the route parameters.
 * 
 * - If the `idcustom` parameter is missing or empty, displays an error toast and redirects to the root path.
 * - If the `idcustom` parameter is present, attempts to fetch the template by ID:
 *   - If the template exists, caches the template data and allows navigation.
 *   - If the template does not exist or an error occurs, displays an error toast and redirects to the root path.
 * 
 * @param route The current `ActivatedRouteSnapshot` containing route parameters.
 * @param state The current `RouterStateSnapshot`.
 * @returns `true` if the template exists and navigation should continue, or a `RedirectCommand` to redirect otherwise.
 */
export const ValidTemplateIdGuard: CanActivateFn = (route, state) => {
  // Inject services
  const toastBar: ToastBarService = inject(ToastBarService);
  const router: Router = inject(Router);
  const templateService = inject(TemplateService);
  const globalTemplateService: GlobalTemplateService = inject(
    GlobalTemplateService
  );

  const idcustom = route.params['idcustom'];
  if (!idcustom || idcustom === '') {
    toastBar.showToast({
      severity: 'error',
      summary: 'Error',
      detail: 'Template ID is empty!',
    });
    return new RedirectCommand(router.parseUrl('/'), {
      skipLocationChange: true,
    });
  } else {
    return templateService.getTemplateById(idcustom).pipe(
      filter((res) => res !== null),
      map((res) => {
        // Template exists, cache the data
        globalTemplateService.cacheTranslateTemplateData(res);

        // Continue directing the user
        return true;
      }),
      catchError((error) => {
        // Handle any errors from the service call
        console.error('Error fetching template:', error);
        toastBar.showToast({
          severity: 'error',
          summary: 'Error',
          detail: 'No template found!',
        });
        return of(
          // Template doesn't exist
          new RedirectCommand(router.parseUrl('/'), {})
        );
      })
    );
  }
};
